import React, { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import config from "../../config.js";
import {
  CircularProgress,
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import SubTitle from "../../components/common/SubTitle";
import Cards from "../../components/common/Cards";
import SearchFilter from "../../components/common/SearchFilter";
import DynamicTable from "../../components/common/DynamicTable";
import Pagination from "../../components/common/Pagination";
import sortByParam from "../../hooks/SortByParam";
import searchInTheTable from "../../hooks/SarchInTable";
import DeleteModal from "../common/DeleteModal";

import DriverInlineForm from "./DriverInlineForm";
import DriverBehaviorPanel from "../driverSafety/DriverBehaviorPanel";
import { t } from "i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { buildAssignDriverVehiclePayload } from "../../utils/driverPayloads";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import { computeFleetDriverMetric } from "../../config/driversModuleCatalog";
import { hasLayoutPermission } from "../../config/vehiclesModuleCatalog";
import {
  buildFleetDriverStatCards,
  buildTableHeadersFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
  getTableFeatures,
} from "../../layout/moduleLayoutUtils";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import { downloadBackendExcel, exportTableToCSV } from "../../utils/exportTableData";
import { getOrganizationId } from "../manageData/reportsApi";

const legacyHeaders = [
  { key: "Driver ID", value: "Driver ID" },
  { key: "Driver Name", value: "Driver Name" },
  { key: "Email", value: "Email" },
  { key: "Vehicle", value: "Vehicle" },
  { key: "DL Number", value: "DL Number" },
  { key: "Phone", value: "Phone" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "10px",
  maxHeight: "90vh",
  overflow: "auto",
};

const DriversMain = ({ school = false } = {}) => {
  const [searchParams] = useSearchParams();
  const openedDriverFromReport = useRef(false);
  const scrolledToDriver = useRef(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedEditId, setExpandedEditId] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [sortParam, setSortParam] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [allVehicles, setAllVehicles] = useState([]);
  const [filteredDriversData, setFilteredDriversData] = useState([]);
  const [filters, setFilters] = useState({});

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const { hasPermission, hasDownloadPermission } = usePermission();
  const canUserDownload = hasDownloadPermission(school ? "school-drivers" : "drivers");
  const { organizationInactive } = useUser();
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const { layout: moduleLayout } = usePageLayout(
    useJsonLayout ? PAGE_IDS.FLEET_DRIVERS : null,
  );

  const layoutHeaders = useMemo(() => {
    if (!moduleLayout) return null;
    return buildTableHeadersFromLayout(moduleLayout, { hasPermission, t });
  }, [moduleLayout, hasPermission]);

  const layoutStatCards = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetDriverStatCards(moduleLayout, driversList);
  }, [moduleLayout, driversList]);

  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout) : []),
    [moduleLayout],
  );

  const tableFeatures = useMemo(() => getTableFeatures(moduleLayout) || {}, [moduleLayout]);
  const tableHeaders = layoutHeaders || legacyHeaders;
  const downloadColumns = tableHeaders
    .filter((header) => header.key !== ACTIONS_COLUMN_ID && header.key !== "actions")
    .map((header) => ({
      key: header.key,
      label: header.value || header.label,
    }));

  const customActions = [];
  const parsedUserState = JSON.parse(localStorage.getItem("userState") || "null");
  const currentOrgName = (parsedUserState?.organization?.name || "RouteEye").replace(/[^a-zA-Z0-9_-]/g, "_");
  const driverExportFileName = `${currentOrgName}_${school ? "School_Drivers" : "Drivers"}`;

  const handleDownloadDrivers = async () => {
    const orgId = parsedUserState?.user?.organizationId || getOrganizationId();
    const downloadFileName = `${driverExportFileName}.xlsx`;

    const downloadParams = {
      organizationId: orgId,
      table: "drivers",
      format: "xlsx",
      includeDetails: true,
      search: filters?.search || undefined,
    };

    if (selectedDriverIds && selectedDriverIds.length > 0) {
      downloadParams.driverIds = selectedDriverIds.join(",");
      downloadParams.ids = selectedDriverIds.join(",");
    }

    try {
      await downloadBackendExcel({
        url: `${config.baseURL}/excel/download`,
        params: downloadParams,
        defaultFileName: downloadFileName,
      });
    } catch (err) {
      console.warn("Backend /excel/download table=drivers failed, trying table=driver:", err);
      try {
        await downloadBackendExcel({
          url: `${config.baseURL}/excel/download`,
          params: { ...downloadParams, table: "driver" },
          defaultFileName: downloadFileName,
        });
      } catch (fallbackErr) {
        console.warn("Backend export not available yet, using client export fallback:", fallbackErr);
        const allDrivers = formatDataForTable(filteredDriversData || []);
        const targetData = selectedDriverIds.length > 0
          ? allDrivers.filter((d) =>
              selectedDriverIds.some((id) => String(id) === String(d.id || d.originalData?.id))
            )
          : allDrivers;

        const detailedHeaders = [
          { key: "Driver ID", label: "Driver ID" },
          { key: "Driver Name", label: "Driver Name" },
          { key: "Email", label: "Email" },
          { key: "Phone", label: "Phone" },
          { key: "DL Number", label: "DL Number" },
          { key: "Vehicle", label: "Vehicle" },
          { key: "licenseExpiry", label: "License Expiry" },
          { key: "address", label: "Address" },
          { key: "emergencyContact", label: "Emergency Contact" },
          { key: "experience", label: "Experience" },
          { key: "site", label: "Site" },
        ];

        exportTableToCSV(
          driverExportFileName,
          detailedHeaders,
          targetData.map((row) => ({
            ...row,
            licenseExpiry: row.originalData?.license_expiry || row.originalData?.DL_expiry || "N/A",
            address: row.originalData?.address || "N/A",
            emergencyContact: row.originalData?.emergency_contact || row.originalData?.emergency_phone || "N/A",
            experience: row.originalData?.experience || "N/A",
            site: row.originalData?.site?.name || row.originalData?.site_id || "N/A",
          }))
        );
      }
    }
  };
  const getAllDrivers = async () => {
    try {
      const parsedData = JSON.parse(localStorage.getItem("userState"));
      const orgId = parsedData?.user?.organizationId;

      const response = await axios.get(
        `${config.baseURL}/driver/organization`,
        {
          params: { organizationId: orgId },
          withCredentials: true,
        }
      );

      console.log("Driver data response:", response.data);
      return response.data || [];
    } catch (error) {
      console.error("Error while fetching the data: ", error);
      throw error;
    }
  };

  const getDriverImageUrl = (documents = []) => {
    const photoDocument = documents?.find((doc) => doc.name === "DriverImage");
    return photoDocument?.location
      ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(photoDocument.location)}`
      : null;
  };

  const transformDriverData = (data) => {
    try {
      return data.map((driver) => ({
          id: driver.id,
          DriverName: `${driver.first_name} ${driver.last_name}`,
          driver_id: driver.driver_id,
          vehicle: driver.vehicle
            ? driver.vehicle.name ||
              driver.vehicle.vehicleName ||
              driver.vehicle.VehicleNumber ||
              "Not Assigned"
            : "Not Assigned",
          vehicleDeviceId: driver.vehicle?.device_id || null,
          vehicle_id: driver.vehicle ? driver.vehicle.id : null,
          DL_number: driver.driving_license_number,
          image_url: getDriverImageUrl(driver.documents),
          email: driver.email,
          phone: driver.phone_number,
          createdAt:
            driver.createdAt ||
            driver.created_at ||
            driver.updatedAt ||
            driver.updated_at ||
            null,
        }));
    } catch (error) {
      console.error("Error while transforming the data:", error);
      return [];
    }
  };

  const handleAddDriver = () => {
    setShowCreateForm(true);
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
  };

  const handleCreateSuccess = () => {
    fetchData();
    setShowCreateForm(false);
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleActionClick = (actionId, rowIndex) => {
    const action = customActions.find((action) => action.id === actionId);
    const item = formatDataForTable(filteredDriversData)[rowIndex];

    console.log("Action clicked:", actionId, rowIndex, "Item:", item);

    if (!action || !item) {
      console.error("Action or item not found:", actionId, rowIndex);
      return;
    }

    switch (actionId) {
      case "assign":
        setSelectedDriver(item.originalData);
        setSelectedVehicle(item.originalData.vehicle_id || "");
        setIsAssignModalOpen(true);
        break;
      case "delete":
        setDriverToDelete(item.originalData);
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDriverToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (driverToDelete) {
      try {
        await axios.delete(`${config.baseURL}/driver`, {
          data: { id: driverToDelete.id },
          withCredentials: true,
        });
        fetchData();
        setIsDeleteModalOpen(false);
        setDriverToDelete(null);
      } catch (error) {
        console.error("Error deleting driver:", error);
      }
    }
  };

  const handleAssignCancel = () => {
    setIsAssignModalOpen(false);
    setSelectedDriver(null);
    setSelectedVehicle("");
  };

  const handleAssignConfirm = async () => {
    if (selectedDriver && selectedVehicle) {
      try {
        await axios.put(
          `${config.baseURL}/driver/vehicle`,
          buildAssignDriverVehiclePayload(selectedDriver.id, selectedVehicle),
          { withCredentials: true }
        );
        fetchData();
        setIsAssignModalOpen(false);
        setSelectedDriver(null);
        setSelectedVehicle("");
      } catch (error) {
        console.error("Error assigning vehicle:", error);
      }
    }
  };

  const handleVehicleChange = (e) => {
    setSelectedVehicle(e.target.value);
  };

  const getAssignedVehicleName = (driver) => {
    const matchedVehicle = allVehicles.find(
      (vehicle) =>
        (driver.vehicle_id != null &&
          String(vehicle.id) === String(driver.vehicle_id)) ||
        (driver.vehicleDeviceId &&
          String(vehicle.device_id) === String(driver.vehicleDeviceId)),
    );
    const resolvedName =
      matchedVehicle?.name ||
      matchedVehicle?.vehicleName ||
      matchedVehicle?.VehicleNumber;

    if (resolvedName) return resolvedName;
    if (driver.vehicle && driver.vehicle !== "Not Assigned") return driver.vehicle;
    return "Not Assigned";
  };

  const sortDrivers = (list, param, direction) => {
    const sorted =
      param === "createdAt"
        ? [...list].sort((a, b) => {
            const aTime = new Date(a.createdAt || 0).getTime() || 0;
            const bTime = new Date(b.createdAt || 0).getTime() || 0;
            if (aTime !== bTime) return aTime - bTime;
            return Number(a.id || 0) - Number(b.id || 0);
          })
        : sortByParam(param, list);

    return direction === "desc" ? [...sorted].reverse() : sorted;
  };

  const formatDataForTable = (data) => {
    return data.map((driver) => ({
      "Driver ID": String(driver.driver_id),
      "Driver Name": String(driver.DriverName),
      Email: String(driver.email || ""),
      Vehicle: getAssignedVehicleName(driver),
      "DL Number": String(driver.DL_number || ""),
      Phone: String(driver.phone || ""),
      id: String(driver.id),
      originalData: driver,
    }));
  };

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const vehiclesResponse = await axios.get(
          `${config.baseURL}/vehicle/organization`,
          {
            withCredentials: true,
          }
        );
        setAllVehicles(vehiclesResponse?.data || []);
        console.log(
          "Vehicles data response:",
          vehiclesResponse?.data?.vehicles
        );
      } catch (error) {
        console.log("Error in fetching vehicles", error);
      }
    };
    fetchVehicles();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getAllDrivers();
      const transformedData = transformDriverData(response);
      setDriversList(transformedData);
      setDrivers(transformedData);

      setFilteredDriversData(
        sortDrivers(transformedData, sortParam, sortDirection),
      );
    } catch (error) {
      console.error("Error while fetching and transforming the data:", error);
      setError("Failed to fetch driver data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (openedDriverFromReport.current) return;
    const driverId = Number(searchParams.get("driverId"));
    if (!Number.isInteger(driverId) || driverId <= 0 || !driversList.length) return;
    const match = driversList.some((driver) => Number(driver.id) === driverId);
    if (!match) return;
    setExpandedEditId(driverId);
    openedDriverFromReport.current = true;
  }, [searchParams, driversList]);

  useEffect(() => {
    if (scrolledToDriver.current || isLoading || expandedEditId == null) return;
    if (String(searchParams.get("driverId") || "") !== String(expandedEditId)) return;
    const timer = window.setTimeout(() => {
      const row = document.getElementById(`driver-row-${expandedEditId}`);
      if (!row) return;
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      scrolledToDriver.current = true;
    }, 200);
    return () => window.clearTimeout(timer);
  }, [expandedEditId, isLoading, filteredDriversData, searchParams]);

  useEffect(() => {
    if (driversList.length) {
      let result = [...driversList];

      result = sortDrivers(result, sortParam, sortDirection);

      if (searchInput && searchInput.trim() !== "") {
        result = result.filter((driver) => {
          const searchLower = searchInput.toLowerCase();
          return (
            driver.DriverName.toLowerCase().includes(searchLower) ||
            driver.email?.toLowerCase().includes(searchLower) ||
            driver.driver_id?.toString().toLowerCase().includes(searchLower) ||
            getAssignedVehicleName(driver).toLowerCase().includes(searchLower) ||
            driver.DL_number?.toLowerCase().includes(searchLower) ||
            driver.phone?.toLowerCase().includes(searchLower)
          );
        });
      }

      if (Object.keys(filters).length) {
        console.log("Filters:", filters);
      }

      setFilteredDriversData(result);
    }
  }, [searchInput, sortParam, sortDirection, filters, driversList, allVehicles]);

  const assignedVehicles = driversList.filter(
    (driver) =>
      driver.vehicle_id != null ||
      (driver.vehicle && driver.vehicle !== "Not Assigned"),
  ).length;
  const notAssignedVehicles = driversList.length - assignedVehicles;

  const getAvailableVehicles = () => {
    return allVehicles.filter((vehicle) => {
      const isAssigned = driversList.some(
        (driver) =>
          driver.vehicle_id === vehicle.id && driver.id !== selectedDriver?.id
      );
      return !isAssigned;
    });
  };

  if (error) {
    return <div>{error}</div>;
  }
  const createAction = actionBarWidgets.find((widget) => widget.actionKey === "createDriver");
  const canCreateDriver =
    moduleLayout && createAction
      ? hasLayoutPermission(createAction.permission, hasPermission)
      : true;
  const pageTitle = getPageTitleKey(moduleLayout) || "Drivers";
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization?.name;

  const legacyStatCards = [
    {
      title: "Total Drivers",
      value: computeFleetDriverMetric("totalDrivers", driversList),
      backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
    {
      title: "Assigned Vehicles",
      value: assignedVehicles,
      backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
    {
      title: "Not Assigned Vehicles",
      value: notAssignedVehicles,
      backgroundColor: CARD_BACKGROUND_COLORS.RED,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
  ];

  const dynamicPageTitle = pageTitle || "Drivers";

  return (
    <div style={{ minHeight: "80vh" }}>
      <SubTitle
        title={dynamicPageTitle}
        path={`${orgName || "organization"} > Drivers`}
        add={
          !organizationInactive && canCreateDriver
            ? createAction?.labelKey || "+ Create Drivers"
            : null
        }
        addPath="/driver/add"
        onAddClick={!organizationInactive && canCreateDriver ? handleAddDriver : null}
      />

      {organizationInactive ? (
        <div className="table_container">
          <InactiveInventoryTable
            title="driverInventory"
            headers={tableHeaders}
            data={formatDataForTable(filteredDriversData || [])}
            loading={isLoading}
            emptyMessage={t("noDataFound")}
            itemLabel="drivers"
          />
        </div>
      ) : (
        <>
          <Cards cardData={layoutStatCards || legacyStatCards} />

          {showCreateForm && (
            <div className="table_container">
              <DriverInlineForm
                onClose={handleCloseCreateForm}
                onSuccess={handleCreateSuccess}
              />
            </div>
          )}

          <div className="table_container">
            <SearchFilter
              searchPlaceholder="Search"
              onSearchChange={handleSearchChange}
              onFilterChange={handleFilterChange}
              dataDownload={true}
              downloadRows={
                selectedDriverIds.length > 0
                  ? formatDataForTable(filteredDriversData || []).filter((d) =>
                      selectedDriverIds.includes(d.id || d.originalData?.id)
                    )
                  : formatDataForTable(filteredDriversData || [])
              }
              downloadColumns={downloadColumns}
              downloadFileName={driverExportFileName}
              onDownload={handleDownloadDrivers}
              downloadDisabled={canUserDownload && selectedDriverIds.length === 0}
              downloadTooltipTitle={
                selectedDriverIds.length === 0
                  ? t("Please select records to download")
                  : `${t("Download")} (${selectedDriverIds.length})`
              }
            />

        <Suspense
          fallback={
            <div className="loader-container">
              <CircularProgress />
            </div>
          }
        >
          {/* {isLoading ? (
            <div className="loader-container">
              <CircularProgress />
            </div>
          ) : ( */}
          <DynamicTable
            headers={tableHeaders}
            data={formatDataForTable(filteredDriversData || [])}
            onActionClick={handleActionClick}
            actions={customActions}
            columnWidths={{ "Driver ID": "150px" }}
            loading={isLoading}
            hideActionsMenu={tableFeatures.hideActionsMenu === true}
            selectable={canUserDownload}
            selectedRowKeys={selectedDriverIds}
            onSelectionChange={setSelectedDriverIds}
            rowKey={(row) => row.id || row.originalData?.id}
            expandedRowId={
              tableFeatures.expandableRows === false
                ? null
                : expandedEditId
                  ? String(expandedEditId)
                  : null
            }
            onExpandClick={
              tableFeatures.expandableRows === false
                ? undefined
                : (row) => {
                    const driverId = row.originalData?.id;
                    setExpandedEditId((prev) => (prev === driverId ? null : driverId));
                  }
            }
            renderExpandedRow={
              tableFeatures.expandableRows === false
                ? undefined
                : (row) => (
                    <>
                      <DriverInlineForm
                        editId={row.originalData?.id}
                        editData={row.originalData}
                        onClose={() => setExpandedEditId(null)}
                        onSuccess={() => {
                          fetchData();
                          setExpandedEditId(null);
                        }}
                      />
                      <DriverBehaviorPanel driverId={row.originalData?.id} showDriver={false} />
                    </>
                  )
            }
          />
          {/* )} */}
        </Suspense>
      </div>
        </>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Driver"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {driverToDelete?.DriverName || "this driver"}?{" "}
            </span>
            {t("This action cannot be undone.")}
          </>
        }
      />

      <Modal
        open={isAssignModalOpen}
        onClose={handleAssignCancel}
        aria-labelledby="assign-vehicle-modal-title"
        aria-describedby="assign-vehicle-modal-description"
      >
        <Box
          sx={{
            ...modalStyle,
            border: "1px solid #A5DCF3",
            boxShadow: "-2px 2px 5px 0px #00000040",
            background: "#FFFFFF",
          }}
        >
          <Box sx={{ mb: 3, textAlign: "center", position: "relative" }}>
            <Typography
              id="assign-vehicle-modal-title"
              variant="h5"
              component="h2"
              align="center"
            >
              <span
                style={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontStyle: "Regular",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Assign Vehicle to")}
              </span>
              <br />
              <span
                style={{
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontStyle: "Medium",
                  fontSize: "28px",
                  color: "#183059",
                }}
              >
                {selectedDriver?.DriverName}
              </span>
            </Typography>
            <Button
              onClick={handleAssignCancel}
              sx={{
                position: "absolute",
                right: -12,
                top: -12,
                minWidth: "auto",
                p: 1,
                fontWeight: "bold",
                fontSize: "1.2rem",
              }}
            >
              ×
            </Button>
          </Box>

          <Box sx={{ mb: 3 }} id="assign-vehicle-modal-description">
            <FormControl fullWidth sx={{ mb: 2 }}>
              {/* <InputLabel id="vehicle-select-label">
                {t("Select Vehicle")}
              </InputLabel> */}
              <Select
                displayEmpty
                labelId="vehicle-select-label"
                id="vehicleSelect"
                placeholder="Select Vehicle"
                IconComponent={(props) => (
                  <svg
                    {...props}
                    width="18"
                    height="10"
                    viewBox="0 0 18 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ pointerEvents: "none", marginRight: "12px" }}
                  >
                    <path
                      d="M17 1L9 9L1 1"
                      stroke="#20A8E0"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                value={selectedVehicle}
                onChange={handleVehicleChange}
                label="Select Vehicle"
                sx={{
                  borderRadius: "8px",
                  height: "43px",
                  border: "1px solid #B7BFCC",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    color: "#414958a0",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: "16px",
                  }}
                >
                  Select a Vehicle
                </MenuItem>
                {getAvailableVehicles().map((vehicle) => (
                  <MenuItem
                    key={vehicle.id}
                    value={vehicle.id}
                    sx={{ textAlign: "center" }}
                  >
                    {vehicle.name}
                  </MenuItem>
                ))}
                {selectedDriver?.vehicle_id &&
                  allVehicles
                    .filter((v) => v.id === selectedDriver.vehicle_id)
                    .map((vehicle) => (
                      <MenuItem
                        key={`current-${vehicle.id}`}
                        value={vehicle.id}
                        sx={{ textAlign: "center", backgroundColor: "#f0f0f0" }}
                      >
                        {vehicle.name || vehicle.vehicleName || vehicle.device_id}{" "}
                        (Current)
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>

            {selectedDriver?.vehicle_id && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  {t("Currently assigned to")}: {getAssignedVehicleName(selectedDriver)}
                </Typography>
              )}
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}
          >
            <Button
              onClick={handleAssignCancel}
              variant="outlined"
              sx={{
                minWidth: 100,
                height: "55px",
                borderRadius: "8px",
                padding: "16px 24px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "20px",
                background: "#F2F2F3",
                border: "1px solid #414958",
                color: "#1F242E",
                "&:hover": {
                  background: "#E5E5E6",
                  border: "1px solid #414958",
                },
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={handleAssignConfirm}
              // disabled={!selectedVehicle}
              sx={{
                minWidth: 100,
                height: "55px",
                borderRadius: "8px",
                textTransform: "none",
                padding: "16px 24px",
                fontWeight: 600,
                fontSize: "20px",
                background: "#F9E719",
                border: "1px solid #414958",
                color: "#1F242E",
                "&:hover": {
                  background: "#E1D016",
                  border: "1px solid #414958",
                },
              }}
            >
              {t("Confirm")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default DriversMain;
